import * as Geo from "@/base/geo"
import * as Models from "@/redux/models"
import { useRootSelector } from "@/redux/useRootSelector"
import * as Sentry from "@sentry/react-native"
import * as turf from "@turf/turf"

import { CENTER_PIVOT_ARROW_PARAMS } from "./center-pivot-path-visualization"

export function useLinearPathParams(deviceActivity: Models.DeviceActivity) {
  const locations = deviceActivity.gpsLocations ?? []
  const configuration = useRootSelector((state) => {
    return Models.deviceConfiguration.selectById(state, deviceActivity.device)
  })

  const pathWidthMm = configuration?.linearSpanWidthMm
  const spanHeading = configuration?.linearSpanHeadingDegrees

  const startPoint = Geo.point(locations[0])
  const endPoint = Geo.point(locations[locations.length - 1])

  const deviceHeading =
    deviceActivity.gpsHeadingsDegrees?.[
      deviceActivity.gpsHeadingsDegrees.length - 1
    ]
  return { deviceHeading, endPoint, pathWidthMm, spanHeading, startPoint }
}

type LinearPathParams = ReturnType<typeof useLinearPathParams>
/**
 * Plot swath on map
 */
export function makePlotSwath<P extends Geo.AnyLatLng>(
  transformPoint: Geo.PointTransformer<P>,
) {
  return ({
    endPoint,
    pathWidthMm,
    spanHeading,
    startPoint,
  }: LinearPathParams) => {
    //
    const corners: P[] = []
    const startLatLng = transformPoint(startPoint)
    const endLatLng = transformPoint(endPoint)

    if (
      typeof pathWidthMm === "number" &&
      typeof spanHeading === "number" &&
      startPoint &&
      endPoint &&
      startLatLng &&
      endLatLng
    ) {
      corners.push(startLatLng)
      corners.push(endLatLng)

      const distanceMeters = pathWidthMm / 1000

      const endProjected = transformPoint(
        endPoint.project({
          direction: spanHeading,
          distanceMeters,
        }),
      )

      const startProjected = transformPoint(
        startPoint.project({
          direction: spanHeading,
          distanceMeters,
        }),
      )

      if (startProjected && endProjected) {
        corners.push(endProjected)
        corners.push(startProjected)
      }
    }
    return corners
  }
}

/**
 * Plot arrow on map
 */
export function makePlotArrow<P extends Geo.AnyLatLng>(
  transformPoint: Geo.PointTransformer<P>,
) {
  return ({
    deviceHeading,
    endPoint,
    pathWidthMm,
    spanHeading,
  }: LinearPathParams) => {
    //
    const arrowLinePath: P[] = []
    const arrowHeadPath: P[] = []

    try {
      if (
        typeof deviceHeading === "number" &&
        typeof pathWidthMm === "number" &&
        typeof spanHeading === "number" &&
        endPoint
      ) {
        // Compute candidate snapped headings
        const headingPlus90 = (spanHeading + 90) % 360
        const headingMinus90 = (spanHeading - 90 + 360) % 360

        // Helper to compute minimal angular distance
        const angularDifference = (a: number, b: number) => {
          const diff = Math.abs(a - b) % 360
          return diff > 180 ? 360 - diff : diff
        }

        // Determine which snapped heading is closer to the actual device heading
        const snappedHeading =
          angularDifference(deviceHeading, headingPlus90) <
          angularDifference(deviceHeading, headingMinus90)
            ? headingPlus90
            : headingMinus90

        // Arrow should start at the end of the swath
        const arrowStart = transformPoint(
          turf.destination(endPoint, pathWidthMm / 2, spanHeading, {
            units: "millimeters",
          }).geometry,
        )

        const swathEnd = Geo.point(
          turf.destination(
            endPoint.coordinates,
            pathWidthMm / 2,
            spanHeading,
            {
              units: "millimeters",
            },
          ).geometry,
        )

        const arrowEnd = swathEnd?.project({
          direction: snappedHeading,
          distanceMeters: CENTER_PIVOT_ARROW_PARAMS.baseLengthMeters,
        })

        const arrowEndLatLng = transformPoint(arrowEnd)
        if (arrowStart && arrowEndLatLng) {
          arrowLinePath.push(arrowStart)
          arrowLinePath.push(arrowEndLatLng)
        }

        const arrowHeadLeft = transformPoint(
          arrowEnd?.project({
            direction:
              snappedHeading + CENTER_PIVOT_ARROW_PARAMS.headAngle,
            distanceMeters: CENTER_PIVOT_ARROW_PARAMS.headSizeMeters,
          }),
        )

        const arrowHeadRight = transformPoint(
          arrowEnd?.project({
            direction:
              snappedHeading - CENTER_PIVOT_ARROW_PARAMS.headAngle,
            distanceMeters: CENTER_PIVOT_ARROW_PARAMS.headSizeMeters,
          }),
        )

        if (arrowHeadLeft && arrowHeadRight && arrowEndLatLng) {
          arrowHeadPath.push(arrowHeadLeft)
          arrowHeadPath.push(arrowEndLatLng)
          arrowHeadPath.push(arrowHeadRight)
        }
      }
    } catch (e) {
      Sentry.captureException(e, {
        tags: {
          source: "makePlotArrow",
        },
      })
    }
    return { arrowHeadPath, arrowLinePath }
  }
}

